import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { DateTime } from "luxon";
import { FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist/es/constants";
import storage from 'redux-persist/es/storage';
import { persistReducer, persistStore } from 'redux-persist';
import storageSession from 'redux-persist/es/storage/session';

import { ajaxController } from "../ajax/controller/AjaxController";
import { isOnlineNow } from "../offline/connectivity";
import { refreshToken } from "./actions/authActions";
import selectedShiftReducer, { setSelectedDate, setSelectedShift } from './reducers/shiftReducer';
import authReducer, { logout } from './reducers/authReducer';
import viewReducer from './reducers/viewReducer';
import themeReducer from './reducers/themeReducer';

const viewPersistConfig = {
  key: 'view',
  storage: storageSession, // Note: This is per tab
};

const rootReducer = combineReducers({
  authReducer: authReducer,
  shift: selectedShiftReducer,
  view: persistReducer(viewPersistConfig, viewReducer),
  theme: themeReducer
});

const rootPersistConfig = {
  key: 'root',
  storage,
  whitelist: ['authReducer'],
  blacklist: ['view'],
};

const persistedReducer = persistReducer(rootPersistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;

// hydrate view slice from sessionStorage if present and redux has defaults
const currentShiftState = store.getState().shift
const storedDate = sessionStorage.getItem("shiftDate");
const storedShift = sessionStorage.getItem("shiftDayOrNight");

// only override if redux has its initial defaults (to avoid resetting when navigating back)
if (storedDate && currentShiftState.selectedDate === DateTime.now().toISODate()) {
  store.dispatch(setSelectedDate(storedDate));
}
if (storedShift && currentShiftState.selectedShift === 'DAY') {
  const shiftCode = storedShift === "D" ? "DAY" : "NIGHT";
  store.dispatch(setSelectedShift(shiftCode));
}

// subscribe to keep sessionStorage in sync when view.selectedDate / selectedShift change
let prevShiftState = store.getState().shift;
store.subscribe(() => {
  const nextShiftState = store.getState().shift;
  const dateChanged = nextShiftState.selectedDate !== prevShiftState.selectedDate;
  const shiftChanged = nextShiftState.selectedShift !== prevShiftState.selectedShift;

  if (dateChanged || shiftChanged) {
    sessionStorage.setItem("shiftDate", nextShiftState.selectedDate);
    const backendCode = nextShiftState.selectedShift === "DAY" ? "D" : "N";
    sessionStorage.setItem("shiftDayOrNight", backendCode);
  }
  prevShiftState = nextShiftState;
});

// setup ajax controller to get token from redux store
ajaxController.setTokenGetter(() => {
  const state = store.getState();
  return state.authReducer;
});

ajaxController.setTokenRefreshCallback(() =>
  store.dispatch<any>(refreshToken(store.getState().authReducer.refreshToken!))
);

// When a 401 can't be recovered (refresh failed / no valid session), sign out so
// the route guard redirects to /login instead of the app spamming failed calls.
ajaxController.setAuthFailureHandler(() => {
  if (!store.getState().authReducer.isAuthenticated) return;

  // Note: never while offline. Logging out strands the crew - there is no logging back in without a
  // connection, and every queued offline edit is behind that login - and a 401 arrived at while
  // offline cannot be trusted anyway. The 401 handler tries again on reconnect.
  if (!isOnlineNow()) {
    console.error('Ignoring an unrecoverable auth failure while offline; keeping the session');
    return;
  }

  console.error('Session could not be recovered; signing out');
  store.dispatch(logout());
});